import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

/**
 * DispatchItemManager - Manage individual dispatch visibility and pinning
 * Allows bulk actions and per-dispatch controls
 */
export default function DispatchItemManager({ dispatches, config, onConfigUpdate }) {
  const [editingDispatch, setEditingDispatch] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editExcerpt, setEditExcerpt] = useState('');

  const handleToggleVisibility = async (dispatchId, currentVisible) => {
    try {
      const { error } = await supabase
        .from('dispatches')
        .update({ portal_visible: !currentVisible })
        .eq('id', dispatchId);

      if (error) throw error;
      
      toast.success(currentVisible ? 'Dispatch hidden from portal' : 'Dispatch visible on portal');
      
      // Force reload by updating config (triggers parent reload)
      onConfigUpdate({ ...config, _reload: Date.now() });
    } catch (error) {
      console.error('Error updating dispatch:', error);
      toast.error('Failed to update dispatch');
    }
  };

  const handleTogglePin = async (dispatchId, currentPinned) => {
    try {
      const { error } = await supabase
        .from('dispatches')
        .update({ portal_pinned: !currentPinned })
        .eq('id', dispatchId);

      if (error) throw error;
      
      toast.success(currentPinned ? 'Dispatch unpinned' : 'Dispatch pinned to top');
      
      // Force reload
      onConfigUpdate({ ...config, _reload: Date.now() });
    } catch (error) {
      console.error('Error updating dispatch:', error);
      toast.error('Failed to update dispatch');
    }
  };

  const handleEditDispatch = (dispatch) => {
    setEditingDispatch(dispatch.id);
    setEditTitle(dispatch.title || '');
    setEditExcerpt(dispatch.excerpt || '');
  };

  const handleSaveEdit = async (dispatchId) => {
    try {
      const { error } = await supabase
        .from('dispatches')
        .update({
          portal_title: editTitle || null,
          portal_excerpt: editExcerpt || null
        })
        .eq('id', dispatchId);

      if (error) throw error;
      
      toast.success('Dispatch updated');
      setEditingDispatch(null);
      
      // Force reload
      onConfigUpdate({ ...config, _reload: Date.now() });
    } catch (error) {
      console.error('Error updating dispatch:', error);
      toast.error('Failed to update dispatch');
    }
  };

  const handleBulkHideOld = async () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    try {
      const oldDispatches = dispatches.filter(d => 
        new Date(d.sent_at) < thirtyDaysAgo
      );

      if (oldDispatches.length === 0) {
        toast.info('No dispatches older than 30 days');
        return;
      }

      const { error } = await supabase
        .from('dispatches')
        .update({ portal_visible: false })
        .in('id', oldDispatches.map(d => d.id));

      if (error) throw error;
      
      toast.success(`Hidden ${oldDispatches.length} old dispatches`);
      
      // Force reload
      onConfigUpdate({ ...config, _reload: Date.now() });
    } catch (error) {
      console.error('Error hiding old dispatches:', error);
      toast.error('Failed to hide old dispatches');
    }
  };

  return (
    <div className="space-y-4">
      {/* Bulk Actions */}
      <div className="bg-white rounded-lg p-3 border border-gray-200">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Bulk Actions</h4>
        <div className="flex gap-2">
          <button
            onClick={handleBulkHideOld}
            className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded"
          >
            Hide Dispatches Older Than 30 Days
          </button>
        </div>
      </div>

      {/* Individual Dispatches */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-gray-900">Individual Dispatches</h4>
        
        {dispatches.length === 0 && (
          <p className="text-sm text-gray-500">No dispatches to manage</p>
        )}

        {dispatches.map((dispatch) => (
          <div 
            key={dispatch.id}
            className="bg-white rounded-lg p-3 border border-gray-200"
          >
            {editingDispatch === dispatch.id ? (
              // Edit Mode
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-gray-600">Portal Title</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Original title will be used if empty"
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Portal Excerpt</label>
                  <textarea
                    value={editExcerpt}
                    onChange={(e) => setEditExcerpt(e.target.value)}
                    placeholder="Optional excerpt for portal display"
                    rows={2}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSaveEdit(dispatch.id)}
                    className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingDispatch(null)}
                    className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <div>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 line-clamp-1">
                      {dispatch.title}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(dispatch.sent_at).toLocaleDateString()} • {dispatch.recipient_count} recipients
                    </p>
                  </div>
                  
                  {dispatch.pinned && (
                    <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded ml-2">
                      Pinned
                    </span>
                  )}
                </div>

                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => handleToggleVisibility(dispatch.id, true)}
                    className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    👁️ Hide
                  </button>
                  <button
                    onClick={() => handleTogglePin(dispatch.id, dispatch.pinned)}
                    className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    📌 {dispatch.pinned ? 'Unpin' : 'Pin'}
                  </button>
                  <button
                    onClick={() => handleEditDispatch(dispatch)}
                    className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    ✏️ Edit Display
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Help Text */}
      <p className="text-xs text-gray-500">
        Tip: Pin important dispatches to keep them at the top. Hidden dispatches won't appear on the portal.
      </p>
    </div>
  );
}

